import ErrorBoundary from '@components/ErrorBoundary';
import { useGlobalSiteHeaderContext } from '@components/GlobalSiteHeader/GlobalSiteHeaderContext';
import { color, font, query, space } from '@design/templateFns';
import { Listing } from '@engelvoelkersamericas/api';
import { Trash2 } from '@icon/Trash2';
import { CircularProgress, ClickAwayListener } from '@mui/material';
import {
  DrawingManager,
  GoogleMap,
  InfoBox,
  Marker,
  MarkerClusterer,
  Polygon,
  useJsApiLoader,
} from '@react-google-maps/api';
import auth from '@util/api/auth';
import evAPI from '@util/api/ev';
import { useCurrency } from '@util/customHooks/useCurrency';
import useIsMobile from '@util/customHooks/useIsMobile';
import { GMAPS_LIBRARIES } from '@util/customHooks/useLocationToGeoCode';
import { useMeasurement } from '@util/customHooks/useMeasurement';
import Image from 'next/legacy/image';
import Link from 'next/link';
import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import {
  EmptyImagePlaceholder,
  FooterInfo,
  Headline,
  InfoBoxDescription,
  InfoHeaderWrapper,
  InfoHeadline,
  InfoItems,
  MapWrapper,
  StyledFavoriteButton,
  StyledInfoBoxCol,
  StyledInfoBoxImage,
  StyledInfoBoxMain,
} from './styles';
import { useTranslations } from 'next-intl';

interface GoogleMapResultsProps {
  center?: { lat: number; lng: number };
  mappedResults: Listing[];
  config: any;
  isFullWidth?: boolean;
  zoomValue: number;
  setCenterAndZoom?: boolean;
  mapPropertyLoader?: boolean;
  isMarkerHover?: boolean;
  showDrawAreaButton?: boolean;
  getFilterByPolygon?: ({ polygon }) => void;
}

const GoogleMapResults: FC<GoogleMapResultsProps> = ({
  center = { lat: 41, lng: -80 },
  mappedResults,
  config,
  zoomValue = 10,
  setCenterAndZoom = true,
  mapPropertyLoader = false,
  isMarkerHover = false,
  showDrawAreaButton = true,
  getFilterByPolygon = () => {},
}) => {
  const t = useTranslations();
  const mapRef = useRef<GoogleMap | null>(null);
  const { getListingPrice } = useCurrency();
  const [activeMarkerPosition, setActiveMarkerPosition] = useState(null);
  const [activeMarker, setActiveMarker] = useState<any | null>(null);
  const [activeCluster, setActiveCluster] = useState<{
    count: number;
    min: number;
    max: number;
    average: number;
  } | null>(null);
  const { convertArea } = useMeasurement();
  const [polygons, setPolygons] = useState<any[]>([]);
  const [refreshMarkersToggle, setRefreshMarkersToggle] = useState(false);
  const [clickedMarkerInfo, setClickedMarkerInfo] = useState<any | null>(null);

  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: config.google.mapKey,
    libraries: GMAPS_LIBRARIES,
  });
  const { toSetModalSection } = useGlobalSiteHeaderContext();
  const [isFav, setIsFav] = useState<boolean | null>(null);

  const isMobile = useIsMobile();
  const polygonRefs = useRef<any[]>([]);
  const activePolygonIndex = useRef(null);
  const drawingManagerRef = useRef(null);

  const api = useRef(new evAPI());

  const options = useMemo(
    () => ({
      disableDefaultUI: true,
      clickableIcons: false,
      streetViewControl: false,
      zoomControlOptions: {
        position: 3,
      },
      zoomControl: true,
    }),
    [],
  );
  const onLoad = useCallback((map) => (mapRef.current = map), []);

  useEffect(() => {
    if (clickedMarkerInfo?.e && clickedMarkerInfo?.activeMarkerData) {
      handleMarkerInfoBox(clickedMarkerInfo);
    }
  }, [clickedMarkerInfo]);

  const handleMarkerClick = ({
    e,
    activeMarkerData,
  }: {
    e: google.maps.MapMouseEvent;
    activeMarkerData: any;
  }) => {
    e?.domEvent?.stopPropagation();
    e?.domEvent?.preventDefault();
    setActiveMarker(null);
    setClickedMarkerInfo({ e, activeMarkerData: activeMarkerData });
  };

  const handleMarkerInfoBox = ({ e, activeMarkerData }) => {
    setActiveCluster(null);
    setActiveMarkerPosition(e.latLng);
    setActiveMarker(activeMarkerData);
    setClickedMarkerInfo(null);
  };

  const handleClusterHover = (cluster) => {
    const clusterMarkers = cluster.markers;
    const markerData = clusterMarkers.map((marker) => ({
      lat: marker.position.lat(),
      lng: marker.position.lng(),
    }));
    const markersUnderCluster = mappedResults.filter((loc) =>
      markerData.find((md) => md.lat == loc.Latitude && md.lng == loc.Longitude),
    );
    setActiveCluster({
      count: markersUnderCluster.length,
      ...calculatePriceRange(markersUnderCluster),
    });
    setActiveMarker(null);
    setActiveMarkerPosition(cluster.getCenter());
  };

  const calculatePriceRange = (arr): { min: number; max: number; average: number } => {
    if (arr.length === 0) {
      throw new Error('Array is empty');
    }

    // Get the converted price of each listing
    const prices: number[] = arr.map((listing) => {
      return getListingPrice({
        originatingSystemName: listing.OriginatingSystemName!,
        country: listing.Country,
        listingCurrency: listing.CurrencyCode,
        standardStatus: listing.StandardStatus,
        listPrice: listing.ListPrice!,
        closePrice: listing.ClosePrice,
        closePriceToDisplay: listing.ClosePriceToDisplay,
      }).rawPrice;
    });

    const min = Math.min(...prices);
    const max = Math.max(...prices);
    const total = prices.reduce((acc, price) => acc + price, 0);
    const average = total / prices.length;

    return { min, max, average };
  };

  const google = window.google;

  const svgMarker = {
    path: 'M2,0c0,1.1-0.9,2-2,2s-2-0.9-2-2s0.9-2,2-2S2-1.1,2,0z',
    fillColor: '#E60000',
    fillOpacity: 1,
    strokeColor: '#fff',
    strokeWeight: 3,
    rotation: 0,
    scale: 5,
  };
  const svgMarkerActive = {
    path: 'M2,0c0,1.1-0.9,2-2,2s-2-0.9-2-2s0.9-2,2-2S2-1.1,2,0z',
    fillColor: '#000',
    fillOpacity: 1,
    strokeColor: '#fff',
    strokeWeight: 3,
    rotation: 0,
    scale: 5,
  };

  const hideTooltipOnMapZoom = () => {
    setActiveMarker(null);
    setActiveCluster(null);
  };

  const onLoadPolygon = (polygon, index) => {
    polygonRefs.current[index] = polygon;
  };

  const onClickPolygon = (index) => {
    activePolygonIndex.current = index;
  };

  const onLoadDrawingManager = (drawingManager) => {
    drawingManagerRef.current = drawingManager;
  };

  const onOverlayComplete = ($overlayEvent) => {
    // @ts-ignore
    drawingManagerRef.current.setDrawingMode(null);
    if ($overlayEvent.type === window.google.maps.drawing.OverlayType.POLYGON) {
      const newPolygon = $overlayEvent.overlay
        .getPath()
        .getArray()
        .map((latLng) => ({ lat: latLng.lat(), lng: latLng.lng() }));

      // start and end point should be same for valid geojson
      const startPoint = newPolygon[0];
      newPolygon.push(startPoint);
      $overlayEvent.overlay?.setMap(null);
      setPolygons([...polygons, newPolygon]);
      getFilterByPolygon({ polygon: newPolygon });
    }
  };

  const onDeleteDrawing = () => {
    // TODO: only for delete on polygon selection incase of multiple
    // const filtered = polygons.filter(
    //   (polygon, index) => index !== activePolygonIndex.current,
    // );
    const filtered = polygons.filter((polygon, index) => index !== 0);
    setPolygons(filtered);
    getFilterByPolygon({ polygon: [] });
  };

  const isPointInPolygon = (latitude, longitude, polygon) => {
    if (typeof latitude !== 'number' || typeof longitude !== 'number') {
      throw new TypeError('Invalid latitude or longitude. Numbers are expected');
    } else if (!polygon || !Array.isArray(polygon)) {
      throw new TypeError('Invalid polygon. Array with locations expected');
    } else if (polygon.length === 0) {
      throw new TypeError('Invalid polygon. Non-empty Array expected');
    }

    const x = latitude;
    const y = longitude;

    let inside = false;
    for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
      const xi = polygon[i][0];
      const yi = polygon[i][1];
      const xj = polygon[j][0];
      const yj = polygon[j][1];

      const intersect = yi > y !== yj > y && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi;
      if (intersect) inside = !inside;
    }

    return inside;
  };

  const onEditPolygon = (index) => {
    const polygonRef = polygonRefs.current[index];
    if (polygonRef) {
      const coordinates = polygonRef
        .getPath()
        .getArray()
        .map((latLng) => ({ lat: latLng.lat(), lng: latLng.lng() }));

      const allPolygons = [...polygons];
      allPolygons[index] = coordinates;
      setPolygons(allPolygons);
      setRefreshMarkersToggle(!refreshMarkersToggle);
    }
  };

  const zoomToPolygon = () => {
    if (mapRef && polygons.length > 0) {
      const bounds = new google.maps.LatLngBounds();
      for (const point of polygons[0]) {
        bounds?.extend(new google.maps.LatLng(point.lat, point.lng));
      }
      // @ts-ignore
      mapRef.current?.fitBounds(bounds);
    }
  };

  const onSaveProperty = async (isFavorite: boolean, listingId, listOfficeMlsId, favId) => {
    try {
      const userData = await auth.getUser();
      if (userData?.attributes?.sub) {
        setIsFav(!isFavorite);
        if (isFavorite) {
          await api.current.deleteSavedListing(userData?.attributes?.sub, listingId, favId);
        } else {
          await api.current.saveListing(userData?.attributes?.sub, listingId, listOfficeMlsId);
        }
      } else {
        toSetModalSection('accountIntro');
      }
    } catch (err) {
      toSetModalSection('accountIntro');
    }
  };

  useEffect(() => {
    if (polygons?.length) zoomToPolygon();
  }, [mappedResults]);

  const drawingManagerOptions = {
    polygonOptions: polygonOptions,
    drawingControl: true,
    drawingControlOptions: {
      position: 3,
      drawingModes: [window.google?.maps?.drawing?.OverlayType?.POLYGON],
    },
  };

  useEffect(() => {
    if (isMobile) {
      setPolygons([]);
    }
  }, [setCenterAndZoom, isMobile]);

  const onCustomButtonClick = () => {
    if (drawingManagerRef.current) {
      // @ts-ignore
      drawingManagerRef.current.setDrawingMode(window.google.maps.drawing.OverlayType.POLYGON);
    }
  };

  const centerByMarkersAvg = useMemo(() => {
    if (mappedResults.length === 0) {
      return { lat: 0, lng: 0 }; // Default center if no markers
    }
    const sumLat = mappedResults
      .filter((l) => l.Latitude)
      .reduce((acc, marker) => acc + marker.Latitude!, 0);
    const sumLng = mappedResults
      .filter((l) => l.Longitude)
      .reduce((acc, marker) => acc + marker.Longitude!, 0);
    const centerLat = sumLat / mappedResults.length;
    const centerLng = sumLng / mappedResults.length;
    if (Number(centerLat) && Number(centerLng)) {
      return { lat: centerLat, lng: centerLng };
    }
    return { ...center };
  }, [mappedResults]);

  const formattedPrice = activeMarker
    ? getListingPrice({
        standardStatus: activeMarker.standardStatus,
        listPrice: activeMarker.listPrice!,
        closePrice: activeMarker.closePrice,
        closePriceToDisplay: activeMarker.closePriceToDisplay,
        originatingSystemName: activeMarker.originatingSystemName,
        country: activeMarker.country,
        listingCurrency: activeMarker.currencyCode,
      }).formattedPrice
    : null;

  return isLoaded ? (
    <MapWrapper $isMarkerHover={isMarkerHover}>
      {mapPropertyLoader && (
        <OverlayLoader>
          <CircularProgress color="inherit" />
        </OverlayLoader>
      )}
      <GoogleMap
        zoom={setCenterAndZoom ? zoomValue : 0}
        center={centerByMarkersAvg}
        mapContainerClassName="map-container"
        options={options}
        onLoad={onLoad}
        onZoomChanged={hideTooltipOnMapZoom}
      >
        {!isMobile && showDrawAreaButton && (
          <>
            {drawingManagerRef.current && polygons && polygons.length ? (
              <DeleteIconContainer data-testid="delete-icon" onClick={onDeleteDrawing}>
                <Trash2 />
              </DeleteIconContainer>
            ) : (
              <DrawAreaButton data-testid="draw-area-button" onClick={onCustomButtonClick}>
                {t('Draw Area')}
              </DrawAreaButton>
            )}
          </>
        )}
        <>
          {mappedResults && (
            <>
              <ErrorBoundary>
                <DrawingManager
                  onLoad={onLoadDrawingManager}
                  onOverlayComplete={onOverlayComplete}
                  options={{
                    ...drawingManagerOptions,
                    drawingControl: false,
                  }}
                />
              </ErrorBoundary>

              {polygons?.map((iterator, index) => (
                <Polygon
                  key={index}
                  onLoad={(event) => onLoadPolygon(event, index)}
                  onMouseDown={() => onClickPolygon(index)}
                  onMouseUp={() => onEditPolygon(index)}
                  options={polygonOptions}
                  paths={iterator}
                  draggable
                  editable
                />
              ))}
              <MarkerClusterer
                clusterClass="cluster"
                onMouseOver={handleClusterHover}
                onMouseOut={() => setActiveMarkerPosition(null)}
                onClick={() => setActiveCluster(null)}
              >
                {(clusterer) => (
                  <>
                    {mappedResults.map((mr, ind) => {
                      return (
                        <Marker
                          key={ind}
                          icon={
                            activeMarker?.listingId === mr?.ListingId ? svgMarkerActive : svgMarker
                          }
                          position={{ lat: Number(mr.Latitude), lng: Number(mr.Longitude) }}
                          clusterer={clusterer}
                          onClick={(e) => handleMarkerClick({ e, activeMarkerData: mr })}
                          onMouseOver={
                            isMarkerHover
                              ? (e) => handleMarkerClick({ e, activeMarkerData: mr })
                              : () => {}
                          }
                          onMouseOut={isMarkerHover ? (e) => setActiveMarker(null) : () => {}}
                        />
                      );
                    })}
                  </>
                )}
              </MarkerClusterer>
              {activeMarkerPosition && (
                <InfoBox
                  position={activeMarkerPosition}
                  zIndex={999}
                  options={{
                    boxStyle: {
                      width: isMobile ? '100%' : '408px',
                      // pixelOffset: true,
                    } as unknown as CSSStyleDeclaration,
                    closeBoxURL: ``,
                    pixelOffset: activeMarker
                      ? new google.maps.Size(-180, 15)
                      : new google.maps.Size(-180, 40),
                  }}
                >
                  <ClickAwayListener onClickAway={() => setActiveMarkerPosition(null)}>
                    <StyledWrapper>
                      {activeMarker && (
                        <StyledInfoBoxWrapper data-testid="info-box-container">
                          <StyledInfoBoxMain>
                            <StyledInfoBoxImage data-testid="info-box-image">
                              <Link href={activeMarker.listUrl}>
                                {activeMarker.imgs[0] ? (
                                  <Image
                                    alt="Google Maps active marker"
                                    src={activeMarker.imgs[0]}
                                    width={135}
                                    height={135}
                                    style={{
                                      maxWidth: '100%',
                                      height: 'auto',
                                    }}
                                  />
                                ) : (
                                  <EmptyImagePlaceholder />
                                )}
                              </Link>
                            </StyledInfoBoxImage>
                            <StyledInfoBoxCol data-testid="info-box-description">
                              <InfoHeaderWrapper>
                                {formattedPrice && (
                                  <InfoHeadline data-testid="info-box-header">
                                    <Link href={activeMarker.listUrl}>{formattedPrice}</Link>
                                  </InfoHeadline>
                                )}
                                <StyledFavoriteButton
                                  $isFavorite={isFav !== null ? isFav : activeMarker.isFavorite}
                                  dataTestId="info-box-favorite-btn"
                                  onClick={() =>
                                    onSaveProperty(
                                      isFav !== null ? isFav : activeMarker.isFavorite,
                                      activeMarker.listingId,
                                      activeMarker.listOfficeMlsId,
                                      activeMarker.favId,
                                    )
                                  }
                                />
                              </InfoHeaderWrapper>
                              <InfoItems data-testid="info-box-address">
                                <Link href={activeMarker.listUrl}>{activeMarker.address}</Link>
                              </InfoItems>
                              <InfoItems data-testid="info-box-property-details">
                                {activeMarker.propertyType && (
                                  <span data-testid="info-box-property-type">
                                    {t(activeMarker.propertyType)}
                                  </span>
                                )}
                                {activeMarker.bedrooms && (
                                  <span data-testid="info-box-bedrooms">
                                    {activeMarker.bedrooms} {t('Beds')}
                                  </span>
                                )}
                                {activeMarker.bathrooms && (
                                  <span data-testid="info-box-bathrooms">
                                    {activeMarker.bathrooms} {t('Baths')}
                                  </span>
                                )}
                                {activeMarker.propertyType === 'Land' && activeMarker.acreage ? (
                                  <span data-testid="info-box-acres">
                                    {activeMarker.acreage} {t('Acres')}
                                  </span>
                                ) : (
                                  activeMarker.squareFootage && (
                                    <span data-testid="info-box-sqft">
                                      {
                                        convertArea(
                                          parseFloat(
                                            String(activeMarker.squareFootage).replace(/,/g, ''),
                                          ),
                                          'sq.ft',
                                        ).formattedArea
                                      }
                                    </span>
                                  )
                                )}
                              </InfoItems>
                              <FooterInfo data-testid="info-box-list-office">
                                {t('Listing by')} {activeMarker.listOfficeName}
                              </FooterInfo>
                            </StyledInfoBoxCol>
                          </StyledInfoBoxMain>
                        </StyledInfoBoxWrapper>
                      )}
                      {activeCluster && (
                        <StyledInfoBoxWrapper>
                          <Headline data-testid="info-box-cluster-header">
                            {t('Click to view')} {activeCluster.count} {t('homes in this area')}
                          </Headline>
                          <InfoBoxDescription data-testid="info-box-cluster-description">
                            {t('Homes here are priced between')}{' '}
                            <strong data-testid="info-box-cluster-price-range">
                              {activeCluster.min} - {activeCluster.max}
                            </strong>
                            , {t('with an average list price of')}{' '}
                            <strong>{activeCluster.average}</strong>.
                          </InfoBoxDescription>
                        </StyledInfoBoxWrapper>
                      )}
                    </StyledWrapper>
                  </ClickAwayListener>
                </InfoBox>
              )}
            </>
          )}
        </>
      </GoogleMap>
    </MapWrapper>
  ) : (
    <></>
  );
};

export default GoogleMapResults;

const OverlayLoader = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;
  top: 0;
  z-index: 9;
  background-color: rgba(0, 0, 0, 0.4);
  display: flex;
  justify-content: center;
  align-items: center;
`;
const StyledInfoBoxWrapper = styled.div`
  background-color: white;
  padding: ${space('lg')};
`;
const StyledWrapper = styled.div`
  width: 408px;
  overflow: hidden;
  @media screen and (${query.below('tablet')}) {
    width: 336px;
  }
`;
const DeleteIconContainer = styled.div`
  background: none padding-box rgb(255, 255, 255);
  display: block;
  border: 0px;
  margin: 0px;
  padding: 4px;
  text-transform: none;
  appearance: none;
  position: absolute;
  right: 4px;
  bottom: 5px;
  cursor: pointer;
  user-select: none;
  direction: ltr;
  overflow: hidden;
  text-align: left;
  color: rgb(0, 0, 0);
  font-size: 11px;
  border-bottom-right-radius: 2px;
  border-top-right-radius: 2px;
  box-shadow: rgba(0, 0, 0, 0.3) 0px 1px 4px -1px;
  z-index: 99999;
`;

const polygonOptions = {
  fillOpacity: 0.3,
  fillColor: '#bbbafc',
  strokeColor: '#1700ff',
  strokeWeight: 2,
  draggable: false,
  editable: false,
};

const DrawAreaButton = styled.button`
  background: ${color('bg')};
  ${font('subhead-sm')};
  border: 1px solid ${color('gray2')};
  color: ${color('fg')};
  padding: ${space('md')};
  justify-content: center;
  align-items: center;
  position: absolute;
  right: 4px;
  cursor: pointer;
  z-index: 9;
  bottom: 5px;
`;
